/**
 * App shell sidebar (APP-014) — Capacitor-aware ownership.
 *
 * This is the **only** sidebar implementation for the customer app. Do not import
 * `@up2stay/up2promo-ui` `sidebar` / `ShellHeader` into shell tabs. The ui package
 * keeps a browser-safe shadcn sidebar for admin/other Vite apps; this fork adds
 * Cap chrome gates (`isCapacitorNative` → null), resizable width, and Ionic shell
 * integration. Extend here — do not create a second app copy or restyle via ui.
 */
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import { Separator } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@up2stay/up2promo-ui';
import { Drawer, DrawerContent, DrawerTitle } from '@up2stay/up2promo-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeftIcon } from 'lucide-react';
import * as React from 'react';

import { useDictionary } from '@/core/providers/dictionary-provider';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

import {
  SIDEBAR_WIDTH_DEFAULT_PX,
  SIDEBAR_WIDTH_MAX_PX,
  SIDEBAR_WIDTH_MIN_PX,
  useSidebar,
  useSidebarOptional,
} from './sidebar-context';

/** Shared easing for gap + panel so collapse/expand stay in sync. */
const SIDEBAR_MOTION =
  'duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] group-data-[resizing=true]/sidebar-wrapper:duration-0 group-data-[resizing=true]/sidebar-wrapper:transition-none';

/** Desktop float inset — canvas breathes around the nav panel (peer to Cap floating tab bar). */
const SIDEBAR_FLOAT_INSET = '0.75rem';

function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  side?: 'left' | 'right';
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  const navigationLabel = useDictionary().sidebar.mainNavigation;
  const isFloating = variant === 'floating' || variant === 'inset';

  if (collapsible === 'none') {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          'bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  // Capacitor uses bottom nav only; no sidebar chrome.
  if (isCapacitorNative()) {
    return null;
  }

  const sidebarInner = (
    <div
      data-sidebar="sidebar"
      data-slot="sidebar-inner"
      className={cn(
        'bg-sidebar text-sidebar-foreground relative flex h-full w-full flex-col transform-gpu',
        // Floating: card-radius panel over studio canvas (not a full-height dock slab).
        'group-data-[variant=floating]:overflow-hidden',
        'group-data-[variant=floating]:rounded-[var(--r-card)]',
        'group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border/80',
        'group-data-[variant=floating]:shadow-[0_10px_32px_-14px_rgba(20,30,50,0.18),0_2px_8px_-4px_rgba(20,30,50,0.06),inset_0_1px_0_rgba(255,255,255,0.65)]',
        'dark:group-data-[variant=floating]:border-sidebar-border',
        'dark:group-data-[variant=floating]:shadow-[0_14px_40px_-16px_rgba(0,0,0,0.65),0_2px_10px_-4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.04)]',
        'group-data-[variant=inset]:rounded-[var(--r-card)] group-data-[variant=inset]:border group-data-[variant=inset]:border-sidebar-border group-data-[variant=inset]:shadow-[var(--shadow-card)]',
      )}
    >
      {children}
    </div>
  );

  // Narrow browser viewport: drawer sidebar (header trigger opens via openMobile).
  if (isMobile) {
    return (
      <Drawer
        open={openMobile}
        onOpenChange={(open) => {
          // Nested logout AlertDialog must not dismiss this drawer — that
          // unmounts NavUser and the confirmation with it (DEV-438).
          if (!open && document.querySelector('[data-logout-confirm-dialog]')) {
            return;
          }
          setOpenMobile(open);
        }}
        direction="left"
        shouldScaleBackground={false}
      >
        <DrawerContent
          scrollable
          fitContent={false}
          className={cn(
            'inset-x-auto inset-y-0 left-0 right-auto top-0 mt-0 h-[100dvh] w-(--sidebar-width) max-h-none rounded-none rounded-r-[var(--r-card)] border-y border-r pb-0',
            '[&>div:first-child]:hidden',
          )}
          viewportClassName="p-0"
        >
          {/* Names the drawer dialog for screen readers (DEV-540). */}
          <DrawerTitle className="sr-only">{navigationLabel}</DrawerTitle>
          {sidebarInner}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <div
      // `xl:` must match SHELL_SIDEBAR_OVERLAY_BREAKPOINT (1280) in use-mobile.ts.
      className="group peer text-sidebar-foreground hidden xl:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      style={
        {
          '--sidebar-float-inset': SIDEBAR_FLOAT_INSET,
        } as React.CSSProperties
      }
    >
      {/* Reserves layout space: panel width + outer float gutters (start + end). */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          'relative bg-transparent transition-[width]',
          SIDEBAR_MOTION,
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          isFloating
            ? 'w-[calc(var(--sidebar-width)+2*var(--sidebar-float-inset))] group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+2*var(--sidebar-float-inset)+(--spacing(4)))]'
            : 'w-(--sidebar-width) group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          // Prefer transform over inset for GPU-composited slide (less jank with blur).
          'fixed z-10 hidden w-(--sidebar-width) transform-gpu transition-transform will-change-transform xl:flex',
          SIDEBAR_MOTION,
          isFloating
            ? cn(
                side === 'left'
                  ? 'start-[var(--sidebar-float-inset)] group-data-[collapsible=offcanvas]:-translate-x-[calc(100%+var(--sidebar-float-inset))]'
                  : 'end-[var(--sidebar-float-inset)] group-data-[collapsible=offcanvas]:translate-x-[calc(100%+var(--sidebar-float-inset))]',
                'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]',
              )
            : cn(
                side === 'left'
                  ? 'start-0 group-data-[collapsible=offcanvas]:-translate-x-full'
                  : 'end-0 group-data-[collapsible=offcanvas]:translate-x-full',
                'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[collapsible=icon]:pl-3 group-data-[collapsible=icon]:pr-2 group-data-[side=left]:border-e group-data-[side=right]:border-s',
              ),
          className,
        )}
        style={{
          ...(isFloating
            ? {
                top: 'var(--sidebar-float-inset)',
                bottom: `max(var(--sidebar-float-inset), env(safe-area-inset-bottom, 0px))`,
                height:
                  'calc(100svh - var(--sidebar-float-inset) - max(var(--sidebar-float-inset), env(safe-area-inset-bottom, 0px)))',
              }
            : {
                top: 0,
                bottom: 'max(0px, env(safe-area-inset-bottom, 0px))',
                height:
                  'calc(100svh - max(0px, env(safe-area-inset-bottom, 0px)))',
              }),
          ...(typeof props.style === 'object' ? props.style : {}),
        }}
        {...props}
      >
        {sidebarInner}
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  variant = 'outline',
  size = 'icon',
  mode = 'icon',
  ...props
}: React.ComponentProps<typeof Button>) {
  const sidebar = useSidebarOptional();
  const toggleLabel = useDictionary().sidebar.toggleSidebar;
  if (!sidebar) {
    return null;
  }

  const { toggleSidebar } = sidebar;

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant={variant}
      size={size}
      mode={mode}
      className={cn(
        'page-header-chrome-button size-11 shrink-0 rounded-full shadow-none',
        className,
      )}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      <PanelLeftIcon className="size-[18px]" />
      <span className="sr-only">{toggleLabel}</span>
    </Button>
  );
}

function SidebarRail({
  className,
  'aria-label': ariaLabel = 'Resize Sidebar',
  title = 'Resize Sidebar',
  ...props
}: React.ComponentProps<'button'> & {
  'aria-label'?: string;
  title?: string;
}) {
  const { open, width, setWidth, setIsResizing } = useSidebar();
  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    currentWidth: number;
  } | null>(null);

  const endDrag = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }
      const finalWidth = drag.currentWidth;
      dragRef.current = null;
      setWidth(finalWidth, { persist: true });
      setIsResizing(false);
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Pointer may already be released.
      }
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    },
    [setIsResizing, setWidth],
  );

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!open || event.button !== 0) {
        return;
      }
      event.preventDefault();
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startWidth: width,
        currentWidth: width,
      };
      setIsResizing(true);
      event.currentTarget.setPointerCapture(event.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [open, setIsResizing, width],
  );

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }
      const isRtl =
        typeof document !== 'undefined' &&
        document.documentElement.dir === 'rtl';
      const delta = isRtl
        ? drag.startX - event.clientX
        : event.clientX - drag.startX;
      const nextWidth = Math.min(
        SIDEBAR_WIDTH_MAX_PX,
        Math.max(SIDEBAR_WIDTH_MIN_PX, Math.round(drag.startWidth + delta)),
      );
      drag.currentWidth = nextWidth;
      setWidth(nextWidth, { persist: false });
    },
    [setWidth],
  );

  const onDoubleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      dragRef.current = null;
      setIsResizing(false);
      setWidth(SIDEBAR_WIDTH_DEFAULT_PX, { persist: true });
      document.body.style.removeProperty('cursor');
      document.body.style.removeProperty('user-select');
    },
    [setIsResizing, setWidth],
  );

  if (!open) {
    return null;
  }

  return (
    <button
      type="button"
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label={ariaLabel}
      aria-valuemin={SIDEBAR_WIDTH_MIN_PX}
      aria-valuemax={SIDEBAR_WIDTH_MAX_PX}
      aria-valuenow={width}
      aria-orientation="vertical"
      role="separator"
      tabIndex={0}
      title={title}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={onDoubleClick}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 16 : 8;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          const isRtl = document.documentElement.dir === 'rtl';
          setWidth(width + (isRtl ? step : -step));
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          const isRtl = document.documentElement.dir === 'rtl';
          setWidth(width + (isRtl ? -step : step));
        } else if (event.key === 'Home') {
          event.preventDefault();
          setWidth(SIDEBAR_WIDTH_MIN_PX);
        } else if (event.key === 'End') {
          event.preventDefault();
          setWidth(SIDEBAR_WIDTH_MAX_PX);
        }
      }}
      className={cn(
        'absolute inset-y-0 z-20 hidden w-3 touch-none after:absolute after:inset-y-0 after:start-1/2 after:w-px after:bg-transparent hover:after:bg-sidebar-border focus-visible:outline-none focus-visible:after:bg-sidebar-ring sm:flex',
        'group-data-[side=left]:-end-1.5 group-data-[side=right]:-start-1.5',
        'cursor-col-resize',
        className,
      )}
      {...props}
    />
  );
}

function SidebarInset({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-inset"
      className={cn(
        'bg-background relative flex w-full flex-1 flex-col',
        'xl:peer-data-[variant=inset]:m-2 xl:peer-data-[variant=inset]:ms-0 xl:peer-data-[variant=inset]:rounded-md xl:peer-data-[variant=inset]:shadow-sm xl:peer-data-[variant=inset]:peer-data-[state=collapsed]:ms-2',
        className,
      )}
      {...props}
    />
  );
}

function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn('bg-background h-8 w-full shadow-none', className)}
      {...props}
    />
  );
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
}

function SidebarFooter({
  className,
  style,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      style={{
        // Add bottom padding to account for browser UI (especially on iPad)
        // env(safe-area-inset-bottom) is automatically provided by the browser when viewport-fit=cover is set
        // Fallback to 1rem if env() is not available (older browsers or desktop)
        paddingBottom: `calc(1rem + env(safe-area-inset-bottom, 1rem))`,
        ...style,
      }}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn('bg-sidebar-border mx-2 w-auto', className)}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col p-2', className)}
      {...props}
    />
  );
}

function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
  const Comp = asChild
    ? (Slot as React.ComponentType<React.ComponentProps<'div'>>)
    : 'div';

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        'text-[color:var(--sidebar-label)] ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.8px] outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
  const Comp = asChild
    ? (Slot as React.ComponentType<React.ComponentProps<'button'>>)
    : 'button';

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute end-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn('w-full text-sm', className)}
      {...props}
    />
  );
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col gap-0.5', className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-3 overflow-hidden rounded-[9px] px-2.5 py-2.5 text-start text-[13.5px] outline-hidden ring-sidebar-ring transition-[width,height,padding,background-color,box-shadow,color,border-color] text-[color:var(--sidebar-muted)] hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pe-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-[image:var(--nav-active-bg)] data-[active=true]:font-semibold data-[active=true]:text-[color:var(--nav-active-fg)] data-[active=true]:border data-[active=true]:border-[color:var(--nav-active-border)] data-[active=true]:[&>svg]:text-[color:var(--brand-active-icon)] data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-[17px] [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_var(--sidebar-border)] hover:bg-sidebar-accent hover:text-sidebar-foreground',
      },
      size: {
        default: 'h-auto min-h-9 text-[13.5px]',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  const Comp = asChild
    ? (Slot as React.ComponentType<React.ComponentProps<'button'>>)
    : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  const Comp = asChild
    ? (Slot as React.ComponentType<React.ComponentProps<'button'>>)
    : 'button';

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute end-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'text-sidebar-foreground pointer-events-none absolute end-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none',
        'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
}) {
  // Fixed width for skeleton (using a stable value instead of random)
  const width = React.useMemo(() => {
    return '70%';
  }, []);

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn('flex h-8 items-center gap-2 rounded-md px-2', className)}
      {...props}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        // End edge aligns with the parent button so plan badges fit (DEV-535).
        'border-sidebar-border ms-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-s ps-2.5 py-0.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn('group/menu-sub-item relative', className)}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild
    ? (Slot as React.ComponentType<React.ComponentProps<'a'>>)
    : 'a';

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'text-[color:var(--sidebar-muted)] ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-foreground active:bg-sidebar-accent active:text-sidebar-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden transition-[background-color,box-shadow,color] focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-inherit',
        'data-[active=true]:bg-[image:var(--nav-active-bg)] data-[active=true]:font-semibold data-[active=true]:text-[color:var(--nav-active-fg)] data-[active=true]:border data-[active=true]:border-[color:var(--nav-active-border)] data-[active=true]:[&>svg]:text-[color:var(--brand-active-icon)]',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

export {
  SidebarProvider,
  useSidebar,
  useSidebarOptional,
} from './sidebar-context';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
};
